import React, { Fragment } from 'react';
import { marked } from 'marked';
import parse from 'html-react-parser';

// Configure marked options for consistent rendering
const markedOptions = {
  breaks: true,
  gfm: true,
  headerIds: false,
  mangle: false,
  silent: true,
};

// Custom renderer to ensure proper styling
const renderer = new marked.Renderer();

// Override link rendering to ensure proper styling and security
renderer.link = (href, title, text) => {
  const safeHref = href.startsWith('http') ? href : `https://${href}`;
  return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" style="color: inherit !important; text-decoration: underline; font-weight: bold;">${text}</a>`;
};

// Override paragraph rendering to avoid extra spacing
renderer.paragraph = text => {
  return `<span>${text}</span>`;
};

// Override list rendering for better styling
renderer.list = (body, ordered) => {
  const tag = ordered ? 'ol' : 'ul';
  const style = ordered
    ? 'margin: 0; padding-left: 1.2em; list-style-type: decimal;'
    : 'margin: 0; padding-left: 1.2em; list-style-type: disc;';
  return `<${tag} style="${style}">${body}</${tag}>`;
};

renderer.listitem = text => {
  return `<li style="margin: 0.2em 0;">${text}</li>`;
};

// Override strong/bold rendering
renderer.strong = text => {
  return `<strong style="font-weight: bold;">${text}</strong>`;
};

// Override em/italic rendering
renderer.em = text => {
  return `<em style="font-style: italic;">${text}</em>`;
};

// Custom function to handle underline (not standard markdown)
const processUnderline = text => {
  // Support both __text__ and <u>text</u> for underline
  return text
    .replace(/<u>(.*?)<\/u>/g, '<span style="text-decoration: underline;">$1</span>')
    .replace(/__(.*?)__/g, '<span style="text-decoration: underline;">$1</span>');
};

// Set the custom renderer
marked.setOptions({
  ...markedOptions,
  renderer,
});

export const parseMarkdown = text => {
  if (!text || typeof text !== 'string') return text;

  try {
    // Process underline first (before marked parses it)
    const textWithUnderline = processUnderline(text);

    // Parse with marked
    const htmlOutput = marked(textWithUnderline);

    // Parse HTML to React elements
    const reactElements = parse(htmlOutput, {
      replace: domNode => {
        // Ensure all links open in new tab
        if (domNode.name === 'a' && !domNode.attribs?.target) {
          return (
            <a
              href={domNode.attribs?.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'inherit !important',
                textDecoration: 'underline',
                fontWeight: 'bold',
              }}
            >
              {domNode.children?.[0]?.data || domNode.attribs?.href}
            </a>
          );
        }
      },
    });

    return reactElements;
  } catch (error) {
    console.error('Error parsing markdown:', error);
    return text;
  }
};

export const MarkdownText = ({ text }) => {
  const parsedContent = parseMarkdown(text);
  return <Fragment>{parsedContent}</Fragment>;
};
